import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Play, Pause, SkipForward, ChevronLeft, Check, Rocket, Radio, Crown, Download, Search, ListPlus, RotateCcw,
} from "lucide-react";

import { api, API_BASE, wsUrl, apiErrorMessage } from "@/lib/api";
import {
  applyStageOptimistic, isLiveBroadcastAvailable, mergeLiveCurrent,
  normalizeDisplayCount, postLiveCurrent, postSequenceAction, probeLocalAnnouncer,
} from "@/lib/liveBroadcast";
import {
  batchFromLiveData, planSequenceSigns, sequenceEntity, sequenceStartingStatement,
} from "@/lib/announcerSequence";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RECONNECT_MS = 1500;

const ACTION_LABELS = {
  start_priority: "Started priority queue",
  start_normal: "Started normal queue",
  start_late: "Started late queue",
  done: "Marked done, moved to next",
  skip: "Skipped",
  previous: "Went back to previous",
  pause: "Paused",
  resume: "Resumed",
  display_1: "Announcer set to 1 name",
  display_3: "Announcer set to 3 names",
  display_6: "Announcer set to 6 names",
};

export default function Backstage() {
  const [state, setState] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [addQueueType, setAddQueueType] = useState("NORMAL");
  const [startFromQueueType, setStartFromQueueType] = useState("NORMAL");
  const [startFromSpecialization, setStartFromSpecialization] = useState("");
  const [waitingSpecializations, setWaitingSpecializations] = useState([]);
  const [loadingSpecializations, setLoadingSpecializations] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [sequenceControl, setSequenceControl] = useState(null);
  const [connected, setConnected] = useState(false);
  const [localAnnouncer, setLocalAnnouncer] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const prevOnStageRef = useRef([]);
  const sequenceControlRef = useRef(null);

  useEffect(() => {
    sequenceControlRef.current = sequenceControl;
  }, [sequenceControl]);

  useEffect(() => {
    if (!isLiveBroadcastAvailable()) return undefined;
    const cancel = probeLocalAnnouncer(setLocalAnnouncer);
    const id = setInterval(() => probeLocalAnnouncer(setLocalAnnouncer), 15000);
    return () => { cancel(); clearInterval(id); };
  }, []);

  useEffect(() => {
    let stopped = false;

    const connect = () => {
      const ws = new WebSocket(wsUrl("/ws/backstage"));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (evt) => {
        try {
          const { type, data } = JSON.parse(evt.data);
          if (type === "current") {
            setState((prev) => mergeLiveCurrent(prev, data));
          } else if (type === "state") {
            setState(data);
          }
        } catch (_e) { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (!stopped) reconnectTimerRef.current = setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => { try { ws.close(); } catch (_e) { /* ignore */ } };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* ignore */ } }
    };
  }, []);

  useEffect(() => {
    if (search.trim().length < 2) { setSearchResults([]); return; }
    const id = setTimeout(() => {
      api.get("/registrations/unqueued", { params: { q: search.trim(), limit: 8 } })
        .then((r) => setSearchResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const loadWaitingSpecializations = async (queueType = startFromQueueType) => {
    setLoadingSpecializations(true);
    try {
      const r = await api.get("/backstage/waiting-specializations", { params: { queue_type: queueType } });
      setWaitingSpecializations(r.data.items || []);
      setStartFromSpecialization((current) => {
        const specs = (r.data.items || []).map((item) => item.specialization);
        return specs.includes(current) ? current : (specs[0] || "");
      });
    } catch (e) {
      setWaitingSpecializations([]);
      setStartFromSpecialization("");
      toast.error(apiErrorMessage(e));
    } finally {
      setLoadingSpecializations(false);
    }
  };

  useEffect(() => {
    loadWaitingSpecializations(startFromQueueType);
  }, [startFromQueueType]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshState = async () => {
    try {
      const r = await api.get("/backstage/state");
      setState(r.data);
      return r.data;
    } catch (_e) {
      return null;
    }
  };

  const syncAnnouncerFromState = (data) => {
    if (data?.live) {
      postLiveCurrent(data.live, data.current, data.current_batch);
    }
  };

  useEffect(() => {
    if (!state) return;
    const nextBatch = batchFromLiveData({
      live: state.live,
      current: state.current,
      current_batch: state.current_batch,
    });
    const previousBatch = prevOnStageRef.current;
    const steps = planSequenceSigns(previousBatch, nextBatch);
    if (steps.length) {
      setSequenceControl((prev) => {
        if (prev?.currentStep === "starting") return prev;
        return {
          steps,
          mode: "sign",
          currentStep: "starting",
          label: sequenceStartingStatement(nextBatch),
          entity: sequenceEntity(nextBatch),
          previousBatch,
        };
      });
      return;
    }

    setSequenceControl((prev) => {
      if (prev?.currentStep === "starting") return prev;
      return null;
    });
    prevOnStageRef.current = nextBatch;
  }, [state]);

  const sendSequenceAction = async (action) => {
    postSequenceAction(action);
    try {
      await api.post("/backstage/announcer-sequence", { action });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const sequenceResume = () => {
    const nextBatch = batchFromLiveData({
      live: state?.live,
      current: state?.current,
      current_batch: state?.current_batch,
    });
    prevOnStageRef.current = nextBatch;
    setSequenceControl(null);
    sendSequenceAction("resume");
  };

  const handleDone = async () => {
    if (pendingAction) return;
    if (sequenceControlRef.current?.currentStep === "starting") {
      sequenceResume();
      toast.success("Announcing names");
      return;
    }
    await act("done");
  };

  const doneButtonLabel = sequenceControl?.currentStep === "starting"
    ? "Done · Announce"
    : "Done · Next";

  const handleStageError = async (e, action) => {
    const status = e?.response?.status;
    if (status === 409 || action === "done" || action.startsWith("start_")) {
      const fresh = await refreshState();
      syncAnnouncerFromState(fresh);
    }
    toast.error(apiErrorMessage(e));
  };

  const act = async (action) => {
    if (pendingAction) return;

    const liveUpdatedAt = state?.live?.updated_at;

    const optimistic = applyStageOptimistic(state, action);
    const startQtype = action.startsWith("start_") ? action.replace("start_", "").toUpperCase() : null;
    const onStageSameQueue = Boolean(
      startQtype
      && state?.current_batch?.length
      && (state.live?.queue_type || "PRIORITY") === startQtype,
    );
    if (optimistic && !onStageSameQueue) {
      setState(optimistic);
      postLiveCurrent(optimistic.live, optimistic.current, optimistic.current_batch);
    }

    setPendingAction(action);
    try {
      const r = await api.post("/backstage/action", { action, live_updated_at: liveUpdatedAt });
      setState(r.data);
      syncAnnouncerFromState(r.data);
      if (action.startsWith("start_")) {
        await loadWaitingSpecializations(r.data.live?.queue_type || startFromQueueType);
      }
      toast.success(ACTION_LABELS[action] || "Done");
    } catch (e) {
      await handleStageError(e, action);
    } finally {
      setPendingAction(null);
    }
  };

  const startFromSpecializationQueue = async () => {
    if (pendingAction || !startFromSpecialization) return;
    setPendingAction("start_from_specialization");
    try {
      const r = await api.post("/backstage/start-from-specialization", {
        queue_type: startFromQueueType,
        specialization: startFromSpecialization,
        live_updated_at: state?.live?.updated_at,
      });
      setState(r.data);
      postLiveCurrent(r.data.live, r.data.current, r.data.current_batch);
      await loadWaitingSpecializations(r.data.live?.queue_type || startFromQueueType);
      toast.success(`Started ${startFromQueueType.toLowerCase()} queue from ${startFromSpecialization}`);
    } catch (e) {
      await handleStageError(e, "start_from_specialization");
    } finally {
      setPendingAction(null);
    }
  };

  const resetSequence = async () => {
    if (pendingAction) return;
    if (!window.confirm("Reset the whole stage sequence? Everyone currently called or announced goes back to WAITING. This cannot be undone.")) {
      return;
    }
    setPendingAction("reset");
    try {
      const r = await api.post("/backstage/reset");
      setState(r.data);
      postLiveCurrent(r.data.live, r.data.current, r.data.current_batch);
      await loadWaitingSpecializations(startFromQueueType);
      toast.success("Sequence reset — everyone is back in WAITING");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setPendingAction(null);
    }
  };

  const requeueAnnounced = async () => {
    if (pendingAction) return;
    if (!window.confirm("Move everyone currently ANNOUNCED back to WAITING? Whoever's on stage right now is not affected.")) {
      return;
    }
    setPendingAction("requeue");
    try {
      const r = await api.post("/backstage/requeue-announced");
      setState(r.data);
      toast.success(`${r.data.moved} student${r.data.moved === 1 ? "" : "s"} moved back to WAITING`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setPendingAction(null);
    }
  };

  const addToSequence = async (reg) => {
    try {
      await api.post(`/registrations/${reg.id}/add-to-sequence`, { queue_type: addQueueType });
      toast.success(`${reg.student_name} added to ${addQueueType} queue`);
      setSearch("");
      setSearchResults([]);
      await refreshState();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const downloadList = (queueType) => {
    window.open(`${API_BASE}/backstage/next-list/export?queue_type=${queueType}`, "_blank");
  };

  const cur = state?.current;
  const live = state?.live || {};
  const displayCount = normalizeDisplayCount(live.display_count);
  const onStage = (state?.current_batch?.length
    ? state.current_batch
    : (cur ? [cur] : [])).slice(0, displayCount);
  const priority = state?.priority || [];
  const normal = state?.normal || [];
  const late = state?.late || [];

  return (
    <div data-testid="backstage-page">
      <PageHeader
        title="Backstage Controller"
        subtitle="Master control for on-stage flow. The announcer projection updates automatically."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={connected ? "secondary" : "destructive"} data-testid="ws-status">
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${connected ? "bg-emerald-500" : "bg-red-500 animate-pulse"}`} />
              {connected ? "Connected" : "Reconnecting…"}
            </Badge>
            {localAnnouncer && (
              <Badge variant="secondary" data-testid="local-announcer-link">
                Same-browser announcer · instant
              </Badge>
            )}
            <Badge variant={live.is_paused ? "destructive" : "secondary"} data-testid="live-status">
              <Radio size={12} className="mr-1" />
              {live.is_paused ? "PAUSED" : "LIVE"} · Queue: {live.queue_type || "PRIORITY"} · {displayCount} on stage
            </Badge>
            <Button
              size="sm" variant="outline" data-testid="btn-requeue-announced"
              disabled={!!pendingAction} onClick={requeueAnnounced}
            >
              <RotateCcw size={14} className={`mr-1 ${pendingAction === "requeue" ? "animate-pulse" : ""}`} /> Requeue Announced
            </Button>
            <Button
              size="sm" variant="outline" data-testid="btn-reset-sequence"
              disabled={!!pendingAction} onClick={resetSequence}
            >
              <RotateCcw size={14} className={`mr-1 ${pendingAction === "reset" ? "animate-pulse" : ""}`} /> Reset Sequence
            </Button>
          </div>
        }
      />

      <section className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-8" data-testid="current-panel">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <p className="kicker">Now on Stage</p>
            <div className="flex rounded-lg border border-border p-1 bg-slate-50" data-testid="display-count-toggle">
              {[1, 3, 6].map((n) => (
                <Button
                  key={n}
                  size="sm"
                  variant={displayCount === n ? "default" : "ghost"}
                  className="h-8 px-3"
                  disabled={!!pendingAction}
                  data-testid={`display-count-${n}`}
                  onClick={() => act(`display_${n}`)}
                >
                  {n} name{n > 1 ? "s" : ""}
                </Button>
              ))}
            </div>
          </div>
          {onStage.length > 0 ? (
            displayCount === 1 && onStage.length === 1 ? (
              <>
                <div className="flex items-center gap-2 mt-3">
                  {onStage[0].queue_type === "PRIORITY" && (
                    <Badge className="bg-amber-500"><Crown size={12} className="mr-1" /> Priority</Badge>
                  )}
                </div>
                <h2 className="font-serif text-5xl leading-tight mt-3" data-testid="current-student-name">{onStage[0].student_name}</h2>
                <p className="text-slate-500 mt-2 text-lg" data-testid="current-htno">{onStage[0].htno}</p>
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div><p className="kicker">Entity</p><p>{onStage[0].entity}</p></div>
                  <div><p className="kicker">Program</p><p>{onStage[0].program}</p></div>
                  <div><p className="kicker">Specialization</p><p>{onStage[0].specialization}</p></div>
                </div>
              </>
            ) : (
              <div className={`grid gap-4 mt-4 ${displayCount >= 6 ? "md:grid-cols-3" : "md:grid-cols-3"}`}>
                {onStage.map((person) => (
                  <div key={person.id || person.registration_id} className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                    {person.queue_type === "PRIORITY" && (
                      <Badge className="bg-amber-500 mb-2"><Crown size={12} className="mr-1" /> Priority</Badge>
                    )}
                    <p className="font-serif text-2xl leading-tight">{person.student_name}</p>
                    <p className="text-slate-500 text-sm mt-1">{person.htno}</p>
                    <p className="text-xs text-slate-500 mt-2">{person.entity} · {person.program}</p>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className="mt-6 text-slate-500">No current student. Use the Start buttons below to begin a queue.</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            <Button data-testid="btn-start-priority" variant="outline" className="h-16 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("start_priority")}>
              <Crown size={16} className={`mr-2 ${pendingAction === "start_priority" ? "animate-pulse" : ""}`} /> Start Priority
            </Button>
            <Button data-testid="btn-start-normal" variant="outline" className="h-16 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("start_normal")}>
              <Rocket size={16} className={`mr-2 ${pendingAction === "start_normal" ? "animate-pulse" : ""}`} /> Start Normal
            </Button>
            <Button data-testid="btn-start-late" variant="outline" className="h-16 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("start_late")}>
              <Rocket size={16} className={`mr-2 ${pendingAction === "start_late" ? "animate-pulse" : ""}`} /> Start Late
            </Button>
          </div>

          <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-4 space-y-3" data-testid="start-from-specialization-panel">
            <p className="kicker">Start from specialization</p>
            <p className="text-xs text-slate-500">
              Jump ahead to the first waiting student in a chosen specialization. Earlier specializations stay in WAITING.
            </p>
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <Select value={startFromQueueType} onValueChange={setStartFromQueueType}>
                <SelectTrigger className="w-full md:w-36" data-testid="start-from-queue-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIORITY">Priority</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="LATE">Late</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={startFromSpecialization}
                onValueChange={setStartFromSpecialization}
                disabled={loadingSpecializations || waitingSpecializations.length === 0}
              >
                <SelectTrigger className="w-full md:flex-1" data-testid="start-from-specialization-select">
                  <SelectValue placeholder={loadingSpecializations ? "Loading…" : "Choose specialization"} />
                </SelectTrigger>
                <SelectContent>
                  {waitingSpecializations.map((item) => (
                    <SelectItem key={item.specialization} value={item.specialization}>
                      {item.specialization} ({item.waiting_count} waiting)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="shrink-0"
                disabled={!!pendingAction || !startFromSpecialization || loadingSpecializations}
                onClick={startFromSpecializationQueue}
                data-testid="btn-start-from-specialization"
              >
                <Rocket size={16} className={`mr-2 ${pendingAction === "start_from_specialization" ? "animate-pulse" : ""}`} />
                Start here
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
            <Button data-testid="btn-previous" variant="outline" className="h-24 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("previous")}>
              <div className={`flex flex-col items-center ${pendingAction === "previous" ? "animate-pulse" : ""}`}><ChevronLeft /><span className="text-sm mt-1">Previous</span></div>
            </Button>
            <Button data-testid="btn-skip" variant="outline" className="h-24 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("skip")}>
              <div className={`flex flex-col items-center ${pendingAction === "skip" ? "animate-pulse" : ""}`}><SkipForward /><span className="text-sm mt-1">Skip</span></div>
            </Button>
            <Button data-testid="btn-done" className="h-24 bg-primary hover:bg-primary/90 disabled:opacity-40" disabled={!!pendingAction} onClick={handleDone}>
              <div className={`flex flex-col items-center ${pendingAction === "done" ? "animate-pulse" : ""}`}><Check /><span className="text-sm mt-1">{doneButtonLabel}</span></div>
            </Button>
            {live.is_paused ? (
              <Button data-testid="btn-resume" className="h-24 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("resume")}>
                <div className={`flex flex-col items-center ${pendingAction === "resume" ? "animate-pulse" : ""}`}><Play /><span className="text-sm mt-1">Resume</span></div>
              </Button>
            ) : (
              <Button data-testid="btn-pause" variant="outline" className="h-24 disabled:opacity-40" disabled={!!pendingAction} onClick={() => act("pause")}>
                <div className={`flex flex-col items-center ${pendingAction === "pause" ? "animate-pulse" : ""}`}><Pause /><span className="text-sm mt-1">Pause</span></div>
              </Button>
            )}
          </div>

          {sequenceControl?.currentStep === "starting" && (
            <div
              className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-2"
              data-testid="sequence-control-panel"
            >
              <p className="kicker text-amber-900">Announcer projection</p>
              <p className="text-base leading-relaxed text-amber-950 font-medium">{sequenceControl.label}</p>
              <p className="text-sm text-amber-900/80">
                Press Done when ready to announce names.
              </p>
            </div>
          )}

          <div className="mt-8 border-t pt-4 flex items-center justify-between text-sm flex-wrap gap-2">
            <span className="text-slate-500">
              Priority: <b className="text-black">{priority.length}</b> · Normal: <b className="text-black">{normal.length}</b> · Late: <b className="text-black">{late.length}</b>
            </span>
          </div>

          <div className="mt-6 border-t pt-4">
            <p className="kicker mb-2 flex items-center gap-2"><Search size={12} /> Manually add to sequence</p>
            <p className="text-xs text-slate-500 mb-3">For a student who didn't check in but still needs to be called.</p>
            <div className="flex gap-2 items-center">
              <Input
                data-testid="backstage-add-search"
                placeholder="Search by roll no. or name… (min 2 chars)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1"
              />
              <Select value={addQueueType} onValueChange={setAddQueueType}>
                <SelectTrigger className="w-32" data-testid="backstage-add-queue-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIORITY">Priority</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="LATE">Late</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-md divide-y max-h-56 overflow-y-auto">
                {searchResults.map((r) => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium">{r.student_name}</p>
                      <p className="text-xs text-slate-500">{r.htno} · {r.specialization}</p>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`backstage-add-${r.htno}`} onClick={() => addToSequence(r)}>
                      <ListPlus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <QueuePanel title="Priority" items={priority} testId="priority-queue" onDownload={() => downloadList("PRIORITY")} accent="amber" />
        <QueuePanel title="Normal" items={normal} testId="normal-queue" onDownload={() => downloadList("NORMAL")} />
        <QueuePanel title="Late" items={late} testId="late-queue" onDownload={() => downloadList("LATE")} />
      </section>
    </div>
  );
}

function QueuePanel({ title, items, testId, onDownload, accent }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="kicker">{title} · {items.length}</p>
        <Button size="icon" variant="ghost" onClick={onDownload} data-testid={`${testId}-download`} title="Download list">
          <Download size={14} />
        </Button>
      </div>
      <div className="max-h-96 overflow-y-auto space-y-2">
        {items.length === 0 && <p className="text-slate-400 text-sm p-4 text-center">Empty</p>}
        {items.map((it) => (
          <div key={it.id || it.registration_id} className={`flex items-center justify-between text-sm px-3 py-2 rounded-md border ${
            it.status === "CURRENT" ? `bg-primary/10 border-primary` :
            it.status === "ANNOUNCED" ? "bg-slate-100 border-slate-200 text-slate-500" :
            accent === "amber" ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
          }`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{it.student_name}</p>
              <p className="text-xs text-slate-500 truncate">{it.htno} · {it.specialization}</p>
            </div>
            <Badge variant="outline" className="ml-2 shrink-0">{it.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
